import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { serializeValue } from "../utils/firestoreValueUtils";

function cellForExport(value) {
  if (value == null) return "";
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return String(value);
    }
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(serializeValue(value));
  return value;
}

/**
 * Flatten { id, data } rows into plain records limited to the visible columns.
 * @param {Array} rows
 * @param {string[]} columns field names (in order), should include "id"
 */
export function rowsToRecords(rows, columns) {
  const fields = columns && columns.length ? columns : ["id"];
  return rows.map((row) => {
    const record = {};
    fields.forEach((field) => {
      record[field] = field === "id" ? row.id : cellForExport(row?.data?.[field]);
    });
    return record;
  });
}

function timestampName(prefix, ext) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${prefix}_${stamp}.${ext}`;
}

export function exportCsv(rows, columns, prefix = "export") {
  const records = rowsToRecords(rows, columns);
  const sheet = XLSX.utils.json_to_sheet(records);
  const csv = XLSX.utils.sheet_to_csv(sheet);
  saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), timestampName(prefix, "csv"));
}

export function exportExcel(rows, columns, prefix = "export") {
  const records = rowsToRecords(rows, columns);
  const sheet = XLSX.utils.json_to_sheet(records);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Data");
  const out = XLSX.write(book, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([out], { type: "application/octet-stream" }), timestampName(prefix, "xlsx"));
}

export function exportJson(rows, prefix = "export") {
  const payload = rows.map((row) => ({ id: row.id, ...serializeValue(row.data || {}) }));
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  saveAs(blob, timestampName(prefix, "json"));
}

/**
 * Export visible rows to a paginated PDF table (jspdf + autotable, loaded on
 * demand so the main bundle stays lean).
 */
export async function exportPdf(rows, columns, prefix = "export", title = "Collection Export") {
  const [jsPdfMod, autoTableMod] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const jsPDF = jsPdfMod.default || jsPdfMod.jsPDF;
  const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;
  const fields = columns && columns.length ? columns : ["id"];
  const records = rowsToRecords(rows, fields);

  const doc = new jsPDF({ orientation: fields.length > 6 ? "landscape" : "portrait" });
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.text(`${records.length} rows · generated ${new Date().toLocaleString()}`, 14, 22);

  autoTable(doc, {
    startY: 26,
    head: [fields],
    body: records.map((record) => fields.map((f) => String(record[f] ?? ""))),
    styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { top: 26, left: 10, right: 10 },
  });

  doc.save(timestampName(prefix, "pdf"));
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function printRows(rows, columns, title = "Collection Export") {
  const records = rowsToRecords(rows, columns);
  const fields = columns && columns.length ? columns : ["id"];
  const head = fields.map((f) => `<th>${escapeHtml(f)}</th>`).join("");
  const body = records
    .map(
      (record) =>
        `<tr>${fields.map((f) => `<td>${escapeHtml(record[f])}</td>`).join("")}</tr>`
    )
    .join("");
  const html = `<!doctype html><html><head><title>${escapeHtml(title)}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#0f172a}
      h1{font-size:18px;margin:0 0 4px}
      p{color:#64748b;margin:0 0 16px;font-size:12px}
      table{border-collapse:collapse;width:100%;font-size:11px}
      th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:left;vertical-align:top}
      th{background:#f1f5f9}
      tr:nth-child(even) td{background:#f8fafc}
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <p>${records.length} rows &middot; generated ${new Date().toLocaleString()}</p>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}
